import { type DependencyList, useEffect } from 'react';

import { type IWebSocketConfig, WsConnection } from './WsConnection';

const registry = new Map<string, WsConnection>();

export function getMemoizedConnection(config: IWebSocketConfig): WsConnection {
  const existing = registry.get(config.url);
  if (existing !== undefined) return existing;
  const conn = new WsConnection(config);
  registry.set(config.url, conn);
  return conn;
}

export type SocketSubscription = { method: string; action: (...args: unknown[]) => void };

/**
 * Returns a React hook that subscribes/unsubscribes from one or more named
 * WebSocket events, mimicking the declarative SignalR `.on()` / `.off()` pattern.
 *
 * Single-event form:
 *   useSlabSocket(() => ({ method: 'ON_DIALOG_UPDATE', action: (payload) => { ... } }), [dep]);
 *
 * Multi-event form (subscribes all at once, cleaned up together):
 *   useSlabSocket(
 *     () => [
 *       { method: 'ON_DIALOG_UPDATE', action: (payload) => { ... } },
 *       { method: 'ON_RECONNECTED',   action: () => refetch() },
 *     ],
 *     [dep],
 *   );
 *
 * The factory is called on mount and whenever deps change. All variables closed
 * over inside the factory must appear in deps — ESLint's exhaustive-deps rule
 * enforces this automatically via additionalHooks.
 */
export function webSocketFactory(config: IWebSocketConfig) {
  return function useWebSocket(
    factory: () => SocketSubscription | SocketSubscription[],
    deps: DependencyList = [],
  ): void {
    const connection = getMemoizedConnection(config);

    useEffect(() => {
      const result = factory();
      const subs = Array.isArray(result) ? result : [result];

      const handlers = subs.map((sub) => {
        const resolved = config.middleware
          ? config.middleware({ method: sub.method, action: sub.action })
          : { method: sub.method, action: sub.action };
        connection.on(resolved.method, resolved.action);
        return resolved;
      });

      return () => {
        handlers.forEach((h) => connection.off(h.method, h.action));
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
  };
}
